import React, { useState } from "react";
import { useAuth } from "../../utility/auth";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Grid,
  InputLabel,
  IconButton,
  FormControlLabel,
  Radio,
} from "@mui/material";
import Controls from "../../components/FormsUI";
import CustomizedSnackbars from "../../components/Alert";
import ImagePreview from "../../components/FormsUI/PreviewImage/index";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import ExceptionHandlerController from "../../controller/ErrorLogsController";
import "../../css/style.css";
import ContentController from "../../controller/ContentController";

const INITIAL_FORM_STATE = { image: [] };
const FORM_VALIDATION = Yup.object().shape({
  image: Yup.array().optional(),
});
const ImagePopup = (props) => {
  const propsData = props.props ?? props;
  const [alert, setAlert] = useState({ counter: 0, type: "", msg: "" });
  const [images, setImages] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imageIds, setImagesId] = useState([]);
  const [coverImageIndex, setCoverImageIndex] = useState(null);
  const [imageCount, setImageCount] = useState(1);
  const auth = useAuth();
  const user = auth.decoder(sessionStorage.getItem("user"));
  const { token } = user;
  const [popupPreview, setPopUpPreview] = useState({
    image0: false,
    image1: false,
    image3: false,
    image4: false,
    image5: false,
  });
  const exceptionHandlerController = new ExceptionHandlerController();
  const contentController = new ContentController();
  const showAlert = (type, msg) => {
    const notify = { ...alert };
    notify.counter = notify.counter + 1;
    notify.type = type;
    notify.msg = msg;
    setAlert(notify);
  };

  React.useEffect(() => {
    if (propsData?.updatedData) {
      if (propsData?.updatedData.image.length !== 0) {
        imageMapping(propsData?.updatedData?.image);
        idsMapping(propsData?.updatedData);
      }
      setCoverImageIndex(propsData?.updatedData?.coverImageIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const imageMapping = async (updatedData) => {
    const mappedImages = updatedData.map((item) => item.image);
    const promises = mappedImages.map(async (imageUrl) => {
      const response = await fetch(imageUrl);
      return response.blob();
    });

    const blobs = await Promise.all(promises);
    setImages(mappedImages);
    setImageCount(mappedImages.length);
    setFiles(blobs);
  };

  const idsMapping = (updatedData) => {
    const ids = updatedData?.image.map((e) => e?._id);
    setImagesId(ids);
  };

  const handleRadioButtonChange = (index) => {
    const newIndex = coverImageIndex === index ? null : index;
    setCoverImageIndex(newIndex);
  };

  const handleImageValidation = async () => {
    const minWidth = 1000;
    const minHeight = 500;
    const file = files[coverImageIndex];

    const fileDataURL = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const img = await new Promise((resolve) => {
      const imagePromise = new Image();
      imagePromise.onload = () => resolve(imagePromise);
      imagePromise.src = fileDataURL;
    });
    return img.width >= minWidth && img.height >= minHeight ? 1 : 0;
  };

  const submitImages = async () => {
    try {
      validateFiles();
      await validateCoverImage();
      const data = prepareData();
      const formData = createFormData(data);
  
      setLoading(true);
      const response = await contentController.UploadImages(formData, token);
      setLoading(false);
  
      handleUploadResponse(response);
    } catch (error) {
      showAlert('error', error.toString());
      handleError(error);
    }
  };
  
  const validateFiles = () => {
    if (files.length === 0) {
      throw new Error("Cannot submit empty image");
    }
    
    if (!propsData?.updatedData?.memberId) {
      const filteredFiles = files.filter(file => file !== null && file !== undefined);
      const hasGaps = files.length !== filteredFiles.length;
  
      if (hasGaps || imageCount !== files.length) {
        throw new Error("No Blank Field is Allowed");
      }
    }
  };
  
  const validateCoverImage = async () => {
    if (coverImageIndex !== null && coverImageIndex !== undefined) {
      const isValidSize = await handleImageValidation();
      if (isValidSize === 0) {
        throw new Error("Cover image dimensions must be at least 1000x500 pixels.");
      }
    }
  };
  
  const prepareData = () => {
    return {
      sectionId: propsData?.updatedData?.sectionId,
      contentId: propsData?.updatedData?.contentId,
      coverImageIndex: coverImageIndex ?? null,
    };
  };
  
  const createFormData = (data) => {
    const formData = new FormData();
    for (const key in data) {
      if (data.hasOwnProperty(key) && key !== 'files') {
        formData.append(key, data[key]);
      }
    }
  
    files.forEach((image, index) => {
      formData.append(`image${index}`, image);
    });
  
    return formData;
  };
  
  const handleUploadResponse = (response) => {
    if (response?.data?.status === 200) {
      showAlert("success", "Image Uploaded successfully");
      propsData.setUpdatedData((prev) => ({
        ...prev,
        image: response?.data?.body ?? [],
      }));
      setTimeout(() => setAlert({ counter: 0, type: "", msg: "" }), 10000);
      propsData.getSectionsGrid();
      setTimeout(() => propsData.handlePopup1(false), 2000);
    } else {
      showAlert("error", response?.response?.data?.message);
    }
  };
  
  const handleError = (error) => {
    const logData = {
      message: error?.message,
      stack: error?.stack,
    };
    const logError = {
      logData,
      module: "ImagePopup manage submitImages method",
    };
    exceptionHandlerController.HandleException(logError);
  };
  
  const saveFile = ({ e, setFieldValue, field, index }) => {
    let caughtError;
    try {
      const fileSizeInBytes = e?.target?.files[0]?.size;
      const fileSizeInKB = fileSizeInBytes / 1024;
      const fileSizeInMB = fileSizeInKB / 1024;
      let MB = fileSizeInMB?.toFixed(2);
      if (MB > 4) {
        showAlert("error", "Document size should be not more than 4MB");
        return false;
      }
      let filename = e?.target?.files[0]?.name?.split(".");
      const extFile = filename?.pop()?.toLowerCase();
      const validExtensions = [
        "jpg",
        "jpeg",
        "png",
        "jfif",
        "bmp",
        "tiff",
        "tif",
        "webp",
      ];
      if (validExtensions.includes(extFile)) {
        let documentImg = "";
        let reader = new FileReader();
        reader.readAsDataURL(e?.target?.files[0]);
        reader.onload = function () {
          const img = new Image();
          img.onload = function () {
            if (field === `image${index}`) {
              setFieldValue(`image${index}`, documentImg);
              setImages((prevImages) => {
                const updatedImages = [...prevImages];
                updatedImages[index] = documentImg;
                return updatedImages;
              });
              setFiles((prevImages) => {
                const updatedImages = [...prevImages];
                updatedImages[index] = e?.target?.files[0];
                return updatedImages;
              });
            }
          };
          documentImg = reader?.result;
          img.src = documentImg;
        };
        reader.onerror = function (error) {
          showAlert("error", "This File is not Readable");
          setTimeout(() => setAlert({ counter: 0, type: "", msg: "" }), 10000);
        };
      } else {
        showAlert("error", "Only jpeg, png and jpg images are allowed");
        setTimeout(() => setAlert({ counter: 0, type: "", msg: "" }), 10000);
      }
    } catch (error) {
      caughtError = error;
      let logData = {};
      logData.message = caughtError?.message;
      logData.stack = caughtError?.stack;
      let logError = { logData, module: "Content Manage saveFile method" };
      exceptionHandlerController.HandleException(logError);
    }
  };

  const onClickHandler = () => {
    if (imageCount < 5) {
      setImageCount(imageCount + 1);
    }
  };
  const onDeleteHandler = (index) => {
    if (imageCount) {
      handleDeleteImage(index);
      if (imageCount !== 1) {
        setImageCount(imageCount - 1);
      }
      const updatedImages = [...images];
      updatedImages.splice(index, 1);
      setImages(updatedImages);
      files.splice(index, 1);
      setFiles(files);
      setCoverImageIndex(null);
    }
  };
  const handleDeleteImage = (id) => {
    if (imageIds[id]) {
      let data = {};
      data.imageId = imageIds[id];
      data.contentId = propsData?.updatedData?.contentId;
      contentController.DeleteImagebyId(data, token).then((resultObj) => {
        if (resultObj.status === 200) {
          propsData.setUpdatedData((prev) => ({
            ...prev,
            image: resultObj?.data?.body ?? [],
          }));
          showAlert("success", "Deleted successfully");
          propsData.getSectionsGrid();
        } else {
          showAlert("error", resultObj?.response?.data?.message);
        }
      });
    }
  };
  const previewPopUpImage = (field, value) =>
    setPopUpPreview((prev) => ({ ...prev, [field]: value }));
  return (
    <>
      <Formik
        enableReinitialize
        initialValues={INITIAL_FORM_STATE}
        validationSchema={FORM_VALIDATION}
        onSubmit={() => submitImages()}
      >
        {({ values, setFieldValue, setFieldTouched }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {propsData?.updatedData?.sectionId ? (
                  <Grid item xs={12} md={12}>
                    <b>
                      {`${propsData?.updatedData?.sectionName} > ${propsData?.updatedData?.subSectionName}`}
                    </b>
                  </Grid>
                ) : null}
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} md={12}>
                <h3 className="upload">Upload Image</h3>
              </Grid>
              {Array.from({ length: imageCount }).map((_, index) => (
                <>
                  <Grid item xs={12} md={12}>
                    <Grid className="col-lg-4 col-md-4 l-float">
                      <input
                        type="file"
                        onChange={(e) => {
                          let field = `image${index}`;
                          saveFile({ e, setFieldValue, field, index });
                        }}
                        className="fileupload"
                      />
                    </Grid>
                    <Grid className="col-lg-4 col-md-4 l-float ml-2 radio-cover-left">
                      {!propsData?.updatedData?.memberId && (
                        <Controls.RadioButton name="coverImage">
                          <FormControlLabel
                            value={index}
                            control={
                              <Radio
                                className="coverImage"
                                checked={coverImageIndex === index}
                                onClick={() => handleRadioButtonChange(index)}
                              />
                            }
                            label="Cover Image"
                          />
                        </Controls.RadioButton>
                      )}
                    </Grid>
                    <Grid className="col-lg-4 col-md-4 l-float ml-1 mr-1">
                      {images[index] && (
                        <img
                          src={images[index]}
                          width="55"
                          style={{ cursor: "pointer" }}
                          height="55"
                          onClick={() =>
                            setPopUpPreview((prev) => ({
                              ...prev,
                              [`image${index}`]: true,
                            }))
                          }
                          alt="Logo"
                          className="imgview"
                        />
                      )}
                      <ImagePreview
                        image={images[index]}
                        popup={popupPreview[`image${index}`]}
                        field={`image${index}`}
                        handlePopup={previewPopUpImage}
                      />
                    </Grid>
                    {!propsData?.updatedData?.memberId && (
                      <>
                        <IconButton>
                          <AddIcon
                            type="button"
                            style={{ fontSize: "2rem" }}
                            color="secondary"
                            id="Add"
                            onClick={onClickHandler}
                          />
                        </IconButton>
                      </>
                    )}
                    <IconButton onClick={() => onDeleteHandler(index)}>
                      <DeleteIcon
                        type="button"
                        style={{ fontSize: "1.5rem" }}
                        color="secondary"
                        id="Add"
                      />
                    </IconButton>

                    <ErrorMessage name={`image${index}`}>
                      {(msg) => <div style={{ color: "#d32f2f" }}>{msg}</div>}
                    </ErrorMessage>
                  </Grid>
                </>
              ))}
            </Grid>
            <Grid className="col-lg-12 col-md-12 l-float">
              <p className="ptag">Only jpeg, png and jpg images are allowed.</p>
              <InfoIcon className="infoicon" />
              <InputLabel
                className="infotext"
                sx={{ color: "black", font: "inherit" }}
              >
                Document size should be not more than 4MB
              </InputLabel>
            </Grid>
            <Grid item xs={4} md={2} className="marginAuto">
              <Controls.LoadingButton loading={loading}>
                Submit
              </Controls.LoadingButton>
            </Grid>
            {alert.counter > 0 && (
              <CustomizedSnackbars
                status={alert.counter}
                msg={alert.msg}
                type={alert.type}
              />
            )}
          </Form>
        )}
      </Formik>
    </>
  );
};
export default ImagePopup;
